// ============================================================================
// ReviewPanel — Interner Prüf-Workflow (Foundation)
// ----------------------------------------------------------------------------
// Listet prüfbare Artefakte des Projekts (Kalkulationen, Konzept-Entwürfe,
// Dachplanungen) und erlaubt interne Prüfvermerke:
//   ENTWURF → zur Prüfung → Änderungen nötig (Kommentar Pflicht) | fachlich geprüft
// KEINE Kundenfreigabe, KEIN Versand, KEIN "approved" — rein interner Vermerk.
// Alle Übergänge werden serverseitig auditiert (review.status_changed).
// ============================================================================
import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client.js";

type ReviewStatus = "draft" | "submitted_for_review" | "needs_changes" | "qa_ready";
type ArtifactType = "cost_calculation" | "konzept_draft" | "roof_design";

interface ArtifactReview {
  id: string;
  artifactType: ArtifactType;
  artifactId: string;
  status: ReviewStatus;
  comment: string | null;
  updatedAt: string;
}

interface ReviewableArtifact {
  artifactType: ArtifactType;
  artifactId: string;
  label: string;
  meta: string;
}

const STATUS_LABELS: Record<ReviewStatus, string> = {
  draft: "ENTWURF",
  submitted_for_review: "Zur Prüfung eingereicht",
  needs_changes: "Änderungen nötig",
  qa_ready: "fachlich geprüft (QA)",
};

const STATUS_COLORS: Record<ReviewStatus, { bg: string; fg: string }> = {
  draft: { bg: "#f3f4f6", fg: "#374151" },
  submitted_for_review: { bg: "#dbeafe", fg: "#1d4ed8" },
  needs_changes: { bg: "#fef3c7", fg: "#b45309" },
  qa_ready: { bg: "#d1fae5", fg: "#047857" },
};

const TYPE_LABELS: Record<ArtifactType, string> = {
  cost_calculation: "💶 Kalkulation",
  konzept_draft: "📄 Konzept-Entwurf",
  roof_design: "🏠 Dachplanung",
};

/** API-Listen tolerant entpacken: direktes Array ODER { items: [...] }. */
function unwrapList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object" && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "–";
  }
}

export function ReviewPanel({ projectId }: { projectId: string }) {
  const [artifacts, setArtifacts] = useState<ReviewableArtifact[]>([]);
  const [reviews, setReviews] = useState<Map<string, ArtifactReview>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Kommentar-Eingabe für "Änderungen nötig" (key = type:id)
  const [commentFor, setCommentFor] = useState<string | null>(null);
  const [commentText, setCommentText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [calcsRes, draftsRes, roofsRes, reviewsRes] = await Promise.all([
      apiClient.get<unknown>(`/projects/${projectId}/costing/calculations`),
      apiClient.get<unknown>(`/projects/${projectId}/konzept/drafts`),
      apiClient.get<unknown>(`/projects/${projectId}/roof-designs`),
      apiClient.get<unknown>(`/projects/${projectId}/reviews`),
    ]);

    if (!calcsRes.success || !draftsRes.success || !roofsRes.success || !reviewsRes.success) {
      const failed = [calcsRes, draftsRes, roofsRes, reviewsRes].find((r) => !r.success);
      setError(failed && !failed.success ? failed.error.message : "Artefakte konnten nicht geladen werden.");
      setLoading(false);
      return;
    }

    const calcs = unwrapList<{ id: string; name: string; version: number; updatedAt: string }>(calcsRes.data);
    const drafts = unwrapList<{ id: string; kind: string; profile?: string | null; createdAt: string }>(draftsRes.data);
    const roofs = unwrapList<{ id: string; name: string | null; version: number; createdAt: string }>(roofsRes.data);

    const rows: ReviewableArtifact[] = [
      ...calcs.map((c) => ({
        artifactType: "cost_calculation" as const,
        artifactId: c.id,
        label: c.name,
        meta: `v${c.version} · ${formatDate(c.updatedAt)}`,
      })),
      ...drafts.map((d) => ({
        artifactType: "konzept_draft" as const,
        artifactId: d.id,
        label: `Konzept (${d.kind}${d.profile ? `, ${d.profile}` : ""})`,
        meta: formatDate(d.createdAt),
      })),
      ...roofs.map((r) => ({
        artifactType: "roof_design" as const,
        artifactId: r.id,
        label: r.name ?? "Dachplanung",
        meta: `v${r.version} · ${formatDate(r.createdAt)}`,
      })),
    ];
    setArtifacts(rows);

    const map = new Map<string, ArtifactReview>();
    for (const rev of unwrapList<ArtifactReview>(reviewsRes.data)) {
      map.set(`${rev.artifactType}:${rev.artifactId}`, rev);
    }
    setReviews(map);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function setStatus(a: ReviewableArtifact, status: ReviewStatus, comment?: string) {
    const key = `${a.artifactType}:${a.artifactId}`;
    setBusy(key);
    setError(null);
    const res = await apiClient.put<ArtifactReview>(
      `/projects/${projectId}/reviews/${a.artifactType}/${a.artifactId}`,
      comment !== undefined ? { status, comment } : { status },
    );
    setBusy(null);
    if (!res.success) {
      setError(res.error.message || "Status konnte nicht gesetzt werden.");
      return;
    }
    const saved = res.data;
    setReviews((prev) => {
      const next = new Map(prev);
      next.set(key, saved);
      return next;
    });
    setCommentFor(null);
    setCommentText("");
  }

  if (loading) return <p style={{ color: "var(--color-text-muted)" }}>Lade prüfbare Artefakte…</p>;

  return (
    <div data-testid="review-panel">
      <div
        style={{
          padding: "10px 14px", marginBottom: 16, borderRadius: 8,
          background: "#eff6ff", border: "1px solid #bfdbfe", fontSize: 13, color: "#1e40af",
        }}
      >
        🔍 <strong>Interner Prüf-Workflow.</strong> Vermerke gelten nur intern — keine Kundenfreigabe,
        kein Versand. Alle Artefakte bleiben ENTWURF bis zur menschlichen Prüfung; jeder Übergang
        wird im Audit-Log protokolliert.
      </div>

      {error && (
        <div style={{ padding: "8px 12px", marginBottom: 12, borderRadius: 6, background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}

      {artifacts.length === 0 ? (
        <p style={{ color: "var(--color-text-muted)", fontSize: 14 }}>
          Noch keine prüfbaren Artefakte vorhanden. Gespeicherte Kalkulationen, Konzept-Entwürfe
          und Dachplanungen erscheinen hier automatisch.
        </p>
      ) : (
        <table data-testid="review-rows" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid var(--color-border)" }}>
              <th style={{ padding: "6px 8px" }}>Artefakt</th>
              <th style={{ padding: "6px 8px" }}>Typ</th>
              <th style={{ padding: "6px 8px" }}>Stand</th>
              <th style={{ padding: "6px 8px" }}>Prüf-Status</th>
              <th style={{ padding: "6px 8px" }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {artifacts.map((a) => {
              const key = `${a.artifactType}:${a.artifactId}`;
              const review = reviews.get(key);
              const status: ReviewStatus = review?.status ?? "draft";
              const colors = STATUS_COLORS[status];
              const isBusy = busy === key;
              return (
                <tr key={key} style={{ borderBottom: "1px solid var(--color-border)", verticalAlign: "top" }}>
                  <td style={{ padding: "8px" }}>{a.label}</td>
                  <td style={{ padding: "8px", whiteSpace: "nowrap" }}>{TYPE_LABELS[a.artifactType]}</td>
                  <td style={{ padding: "8px", whiteSpace: "nowrap", color: "var(--color-text-muted)" }}>{a.meta}</td>
                  <td style={{ padding: "8px" }}>
                    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 12, fontWeight: 600, background: colors.bg, color: colors.fg }}>
                      {STATUS_LABELS[status]}
                    </span>
                    {review?.comment && (
                      <div style={{ marginTop: 4, fontSize: 12, color: "var(--color-text-muted)", maxWidth: 320 }}>
                        💬 {review.comment}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "8px" }}>
                    {commentFor === key ? (
                      <div style={{ display: "flex", gap: 6, alignItems: "flex-start", flexWrap: "wrap" }}>
                        <textarea
                          data-testid="review-comment-input"
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          placeholder="Prüfer-Kommentar (Pflicht): Was ist zu ändern?"
                          rows={2}
                          style={{ minWidth: 220, fontSize: 12, padding: 6, border: "1px solid var(--color-border)", borderRadius: 6 }}
                        />
                        <button
                          data-testid="review-needs-changes-confirm"
                          disabled={isBusy || commentText.trim().length < 3}
                          onClick={() => void setStatus(a, "needs_changes", commentText.trim())}
                          style={{ padding: "4px 10px", fontSize: 12, borderRadius: 6, border: "1px solid #b45309", background: "#fef3c7", color: "#b45309", cursor: "pointer" }}
                        >
                          Änderungen nötig bestätigen
                        </button>
                        <button
                          onClick={() => { setCommentFor(null); setCommentText(""); }}
                          style={{ padding: "4px 10px", fontSize: 12, borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer" }}
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {status !== "submitted_for_review" && (
                          <button
                            data-testid="review-submit"
                            disabled={isBusy}
                            onClick={() => void setStatus(a, "submitted_for_review")}
                            style={{ padding: "4px 10px", fontSize: 12, borderRadius: 6, border: "1px solid #1d4ed8", background: "#dbeafe", color: "#1d4ed8", cursor: "pointer" }}
                          >
                            Zur Prüfung
                          </button>
                        )}
                        <button
                          data-testid="review-needs-changes"
                          disabled={isBusy}
                          onClick={() => { setCommentFor(key); setCommentText(review?.comment ?? ""); }}
                          style={{ padding: "4px 10px", fontSize: 12, borderRadius: 6, border: "1px solid #b45309", background: "transparent", color: "#b45309", cursor: "pointer" }}
                        >
                          Änderungen nötig
                        </button>
                        {status !== "qa_ready" && (
                          <button
                            data-testid="review-qa-ready"
                            disabled={isBusy}
                            onClick={() => void setStatus(a, "qa_ready")}
                            style={{ padding: "4px 10px", fontSize: 12, borderRadius: 6, border: "1px solid #047857", background: "transparent", color: "#047857", cursor: "pointer" }}
                          >
                            Fachlich geprüft
                          </button>
                        )}
                        {status !== "draft" && (
                          <button
                            data-testid="review-reset-draft"
                            disabled={isBusy}
                            onClick={() => void setStatus(a, "draft")}
                            style={{ padding: "4px 10px", fontSize: 12, borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-muted)", cursor: "pointer" }}
                          >
                            Zurück auf ENTWURF
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <p style={{ marginTop: 16, fontSize: 12, color: "var(--color-text-muted)" }}>
        Hinweis: „fachlich geprüft (QA)" ist ein interner Vermerk — keine Freigabe an Kunden,
        keine rechtliche Wirkung. Menschliche Prüfung erforderlich.
      </p>
    </div>
  );
}
